'use client'

import Link from 'next/link'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import PhoneInput from '@/components/ui/phone-input'
import {
  Mail,
  Lock,
  User,
  ArrowRight,
  Phone,
  Building2,
  MapPin,
} from 'lucide-react'
import AuthHeader from '@/components/auth/AuthHeader'
import { useSignup } from '@/hooks/use-auth'
import { useForm, Controller } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

export default function Signup() {
  const schema = z
    .object({
      firstName: z.string().min(2, 'First name is required'),
      lastName: z.string().min(2, 'Last name is required'),
      email: z
        .string()
        .min(1, 'Email is required')
        .email('Please enter a valid email address'),
      mobile: z
        .string()
        .min(1, 'Mobile number is required')
        .regex(/^\+?[1-9]\d{7,14}$/, 'Please enter a valid phone number'),
      role: z.string().min(1, 'Role is required'),
      instituteName: z.string().min(1, 'Institute name is required'),
      instituteAddress: z.string().min(5, 'Institute address must be at least 5 characters long'),
      password: z
            .string()
            .min(8, 'Password must be at least 8 characters long')
            .regex(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
              'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
            ),
      confirmPassword: z.string().min(1, 'Please confirm your password'),
      agree: z
        .boolean()
        .refine((v) => v, { message: 'Please accept the terms and conditions' }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ['confirmPassword'],
      message: 'Passwords do not match',
    })

  type FormValues = z.infer<typeof schema>

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      role: '',
      instituteName: '',
      instituteAddress: '',
      password: '',
      confirmPassword: '',
      agree: false,
    },
  })
  const signupMutation = useSignup()

  const onSubmit = (data: FormValues) => {
    signupMutation.mutate({
      email: data.email.trim(), // Trim spaces from email
      password: data.password, // Don't trim password - spaces might be intentional
      confirm_password: data.confirmPassword, // Don't trim password
      first_name: data.firstName.trim(),
      last_name: data.lastName.trim(),
      mobile_number: data.mobile.trim(),
      institute_name: data.instituteName.trim(),
      institute_address: data.instituteAddress.trim(),
      accept_terms: data.agree,
    })
  }

  return (
    <>
      {/* Header */}
      <AuthHeader badge='Create your educator account' />

      {/* Signup Card */}
      <Card className='shadow-elegant border-border/50'>
        <CardHeader className='space-y-1 text-center'>
          <CardTitle className='text-2xl font-bold text-foreground'>
            Get Started
          </CardTitle>
          <CardDescription className='text-muted-foreground'>
            Start your free trial and explore AI-powered educational insights
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
            {/* Name */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='flex flex-col gap-2'>
                <Input
                  id='firstName'
                  placeholder='Sarah'
                  label='First Name'
                  startIcon={<User className='h-4 w-4' />}
                  className={errors.firstName ? 'border-destructive' : ''}
                  aria-invalid={!!errors.firstName}
                  {...register('firstName')}
                />
                {errors.firstName && (
                  <p className='text-sm text-destructive'>{errors.firstName.message}</p>
                )}
              </div>
              <div className='flex flex-col gap-2'>
                <Input
                  id='lastName'
                  placeholder='Johnson'
                  label='Last Name'
                  className={errors.lastName ? 'border-destructive' : ''}
                  aria-invalid={!!errors.lastName}
                  {...register('lastName')}
                />
                {errors.lastName && (
                  <p className='text-sm text-destructive'>{errors.lastName.message}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <Input
              id='email'
              type='email'
              placeholder='educator@school.edu'
              label='Email'
              startIcon={<Mail className='h-4 w-4' />}
              className={errors.email ? 'border-destructive' : ''}
              aria-invalid={!!errors.email}
              {...register('email')}
            />
            {errors.email && (
              <p className='text-sm text-destructive'>{errors.email.message}</p>
            )}

            {/* Mobile */}
            <div>
              <PhoneInput
                label={<span className='text-sm text-muted-foreground'>Mobile Number</span>}
                nameCountry='signupMobileCountry'
                nameDialCode='signupMobileDial'
                nameNumber='signupMobileNumber'
                onChangeAction={({ dialCode, number }) => {
                  const normalized = `${dialCode}${(number || '').replace(/\s|-/g, '')}`
                  setValue('mobile', normalized, { shouldValidate: true })
                }}
              />
            </div>
            {errors.mobile && (
              <p className='text-sm text-destructive'>{errors.mobile.message}</p>
            )}

            {/* Institute Info */}
            <Input
              id='role'
              placeholder='e.g. Teacher'
              label='Role'
              startIcon={<User className='h-4 w-4' />}
              className={errors.role ? 'border-destructive' : ''}
              aria-invalid={!!errors.role}
              {...register('role')}
            />
            {errors.role && (
              <p className='text-sm text-destructive'>{errors.role.message}</p>
            )}
            <Input
              id='instituteName'
              placeholder='Springfield High School'
              label='Institute Name'
              startIcon={<Building2 className='h-4 w-4' />}
              className={errors.instituteName ? 'border-destructive' : ''}
              aria-invalid={!!errors.instituteName}
              {...register('instituteName')}
            />
            {errors.instituteName && (
              <p className='text-sm text-destructive'>{errors.instituteName.message}</p>
            )}

            <Input
              id='instituteAddress'
              placeholder='742 Evergreen Terrace, Springfield'
              label='Institute Address'
              startIcon={<MapPin className='h-4 w-4' />}
              className={errors.instituteAddress ? 'border-destructive' : ''}
              aria-invalid={!!errors.instituteAddress}
              {...register('instituteAddress')}
            />
            {errors.instituteAddress && (
              <p className='text-sm text-destructive'>{errors.instituteAddress.message}</p>
            )}

            {/* Password */}

            <Input
              id='password'
              type='password'
              placeholder='Create a strong password'
              label='Password'
              startIcon={<Lock className='h-4 w-4' />}
              withPasswordToggle
              className={errors.password ? 'border-destructive' : ''}
              aria-invalid={!!errors.password}
              {...register('password')}
            />
            {errors.password && (
              <p className='text-sm text-destructive'>{errors.password.message}</p>
            )}
            <Input
              id='confirmPassword'
              type='password'
              placeholder='Re-enter password'
              label='Confirm Password'
              startIcon={<Lock className='h-4 w-4' />}
              withPasswordToggle
              className={errors.confirmPassword ? 'border-destructive' : ''}
              aria-invalid={!!errors.confirmPassword}
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className='text-sm text-destructive'>{errors.confirmPassword.message}</p>
            )}

            {/* Terms */}
            <div className='flex items-center space-x-2'>
              <Controller
                name='agree'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    id='agree'
                    checked={Boolean(value)}
                    onCheckedChange={(checked) => onChange(Boolean(checked))}
                  />
                )}
              />
              <Label htmlFor='agree' className='text-sm text-muted-foreground'>
                I agree to the{' '}
                <a href='/terms' className='text-primary'>
                  Terms
                </a>{' '}
                and{' '}
                <a href='/privacy' className='text-primary'>
                  Privacy Policy
                </a>
                .
              </Label>
            </div>
            {errors.agree && (
              <p className='text-sm text-destructive'>{errors.agree.message}</p>
            )}

            <Button
              type='submit'
              size='lg'
              className='w-full group'
              disabled={signupMutation.isPending}
            >
              {signupMutation.isPending ? (
                <div className='flex items-center'>
                  <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2' />
                  Creating account...
                </div>
              ) : (
                <>
                  Create Account
                  <ArrowRight className='ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform' />
                </>
              )}
            </Button>
          </form>

          <div className='text-center text-sm text-muted-foreground'>
            Already have an account?{' '}
            <Link href='/login' className='text-primary'>
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </>
  )
}

